import { FC } from 'react';

import { Icon } from '@shared/components/icons';
import { useAuth } from '@shared/state/hooks/useAuth';
import { getAgencyWorkerTeamName } from '@shared/utils/agencyWorkerUtils';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import { Link } from 'react-router-dom';
import aplusBottom from 'src/assets/banner/aplusBottom.png';

import { useTitleHook } from '../hooks/useTitleHook';

/**
 * 지도사 마이페이지.
 * 화면에 비해 정보가 적으니 줄을 촘촘히 쌓지 않고 항목마다 여유를 준다 —
 * 라벨/값 한 줄씩, 버튼은 전폭. 취약 비밀번호 안내는 팝업 없이 여기서만.
 */

const InfoRow: FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-center justify-between gap-4 py-3">
    <span className="shrink-0 text-13 theme-text-8">{label}</span>
    <span className="text-right text-15 font-medium">{children}</span>
  </div>
);

const AgencyWorkerMyPage: FC = () => {
  useTitleHook('마이페이지');
  const { userProfile } = useAuth();
  const weakPassword = userProfile?.security?.isWeakPassword === true;
  const teamName = userProfile ? getAgencyWorkerTeamName(userProfile) : '';

  const { Phone, User } = Icon();

  return (
    <div className="space-y-4 px-4 py-4">
      {/* 인사 */}
      <div className="flex items-center gap-4 rounded-xl px-5 py-5 theme-bg-1">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-100">
          <User className="wh-6" />
        </div>
        <div className="min-w-0">
          <p className="text-lg font-bold leading-6">
            <span className="theme-text-main">{userProfile?.info.name}</span>님
          </p>
          {teamName && <p className="mt-0.5 text-13 theme-text-8">{teamName} 소속</p>}
        </div>
      </div>

      {/* 계정 */}
      <div className="rounded-xl px-5 py-3 theme-bg-1">
        <h5 className="pt-1 pb-2 text-13 font-bold theme-text-8">계정정보</h5>
        <div className="divide-y divide-gray-100">
          <InfoRow label="아이디">{userProfile?.username}</InfoRow>
          <InfoRow label="이름">{userProfile?.info.name}</InfoRow>
          <InfoRow label="연락처">
            <span className="inline-flex items-center gap-1.5">
              <Phone className="wh-4" />
              {phoneNumberStringFormator(userProfile?.info.phoneNumber)}
            </span>
          </InfoRow>
          {teamName && <InfoRow label="소속">{teamName}</InfoRow>}
        </div>
        <div className="space-y-2 pt-3 pb-2">
          <Link
            to="/user/change-password"
            className={`button-rectangle min-h-[44px] rounded-lg border text-14 font-bold ${
              weakPassword
                ? 'border-red-400 bg-red-50 text-red-600'
                : 'theme-text-8 theme-bg-9 theme-border-3'
            }`}
          >
            비밀번호 변경
          </Link>
          {weakPassword && (
            <p className="px-1 text-12 leading-5 text-red-600">
              <span className="font-bold">비밀번호가 취약합니다.</span> 기본값(1234
              등)·연속·반복 숫자·아이디와 같은 값은 피해서 바꿔 주세요.
            </p>
          )}
        </div>
      </div>

      {/* 편리정보(장지·상주말씀)는 사이드바 "편리정보" 메뉴의 개별 페이지로 이동 (/user/prefer/*) */}

      {/* 바텀 배너 */}
      <a href="https://www.apluslife.co.kr/" className="block overflow-hidden rounded-xl">
        <img src={aplusBottom} className="w-full" />
      </a>
    </div>
  );
};

export default AgencyWorkerMyPage;
